'use client'

import type { FC } from 'react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'

type Props = {
  categories: string[]
  tags?: string[]
  activeCategory?: string | null
  activeTag?: string | null
  onFilterChange: (category: string | null, tag: string | null) => void
  className?: string
}

const FilterBar: FC<Props> = ({
  categories,
  tags = [],
  activeCategory,
  activeTag,
  onFilterChange,
  className,
}) => {
  const [pendingKey, setPendingKey] = useState<string | null>(null)

  const handleClick = (cat: string | null, tag: string | null, key: string) => {
    setPendingKey(key)
    onFilterChange(cat, tag)
    // Clear pending state after a short delay (transition is instant now)
    requestAnimationFrame(() => setPendingKey(null))
  }

  const chip = (
    label: string,
    selected: boolean,
    cat: string | null,
    tag: string | null,
    key: string,
  ) => {
    return (
      <Button
        key={key}
        type="button"
        onClick={() => handleClick(cat, tag, key)}
        variant={selected ? 'default' : 'outline'}
        size="sm"
        className={`rounded-full whitespace-nowrap text-xs sm:text-sm shrink-0
                    transition-colors duration-150`}
        aria-pressed={selected}
      >
        {label}
      </Button>
    )
  }

  return (
    <div
      className={`w-full flex flex-col items-center gap-2 sm:gap-3 md:gap-4 ${className ?? ''}`}
      role="group"
      aria-label="Blog filters"
    >
      {/* Categories row - horizontally scrollable on mobile, wraps on larger screens */}
      <div
        className="w-full flex sm:flex-wrap sm:justify-center gap-2
                   overflow-x-auto scrollbar-hide pb-1 sm:pb-0
                   px-1 sm:px-0
                   -mx-1 sm:mx-0"
      >
        {chip('All Posts', !activeCategory && !activeTag, null, null, 'all')}
        {categories.map((cat) => {
          const selected = activeCategory === cat
          return chip(
            cat,
            selected,
            selected ? null : cat,
            activeTag ?? null,
            `cat:${cat}`,
          )
        })}
      </div>
    </div>
  )
}

export default FilterBar
